import { useState, useEffect, useCallback } from 'react'
import { FulfilmentDetail } from '../../../../../models/fulfilment'
import type { AddOrEditAddressButtonType } from '../address/EditAddressButton/EditAddressButton.type'

// Types
type SaleAddressModalState =
  | { isOpen: false }
  | {
      isOpen: true
      formType: AddOrEditAddressButtonType
      fulfilmentDetails?: FulfilmentDetail
      onClose: (fulfilmentDetails?: FulfilmentDetail) => void
      onSave?: (fulfilmentDetails: FulfilmentDetail[]) => void
    }

interface OpenSaleAddressModalProps {
  formType: AddOrEditAddressButtonType
  fulfilmentDetails?: FulfilmentDetail
  onClose?: (fulfilmentDetails?: FulfilmentDetail) => void
  onSave?: (fulfilmentDetails: FulfilmentDetail[]) => void
}

// Module-level state
let modalState: SaleAddressModalState = { isOpen: false }
let listeners: Array<() => void> = []

const notifyListeners = () => {
  listeners.forEach(listener => listener())
}

export const closeSaleAddressModal = () => {
  modalState = { isOpen: false }
  notifyListeners()
}

// Exported for AngularJS integration
export const openSaleAddressModal = (props: OpenSaleAddressModalProps) => {
  modalState = {
    isOpen: true,
    formType: props.formType,
    fulfilmentDetails: props.fulfilmentDetails,
    onClose: (fulfilmentDetails?: FulfilmentDetail) => {
      props.onClose?.(fulfilmentDetails)
      closeSaleAddressModal()
    },
    onSave: props.onSave,
  }
  notifyListeners()
}

// React hook
export const useSaleAddressModal = () => {
  const [, forceUpdate] = useState(0)

  useEffect(() => {
    const listener = () => forceUpdate(prev => prev + 1)
    listeners.push(listener)

    return () => {
      listeners = listeners.filter(l => l !== listener)
    }
  }, [])

  const open = useCallback((props: OpenSaleAddressModalProps) => {
    openSaleAddressModal(props)
  }, [])

  const close = useCallback(() => {
    closeSaleAddressModal()
  }, [])

  return {
    state: modalState,
    openSaleAddressModal: open,
    closeSaleAddressModal: close,
  }
}
